export interface Point {
  x: number;
  y: number;
}

export type Homography3x3 = Float64Array;

export function applyHomography(pt: Point, m: Homography3x3): Point {
  const { x, y } = pt;
  const w = m[6] * x + m[7] * y + m[8];
  if (Math.abs(w) < 1e-12) return { x: 0, y: 0 };
  return {
    x: (m[0] * x + m[1] * y + m[2]) / w,
    y: (m[3] * x + m[4] * y + m[5]) / w,
  };
}

function gaussSolve(a: Float64Array, b: Float64Array, n: number): Float64Array | null {
  const M = new Float64Array(a);
  const rhs = new Float64Array(b);
  for (let i = 0; i < n; i += 1) {
    let pivotRow = i;
    let maxAbs = Math.abs(M[i * n + i]);
    for (let k = i + 1; k < n; k += 1) {
      const abs = Math.abs(M[k * n + i]);
      if (abs > maxAbs) {
        maxAbs = abs;
        pivotRow = k;
      }
    }
    if (maxAbs < 1e-12) return null;
    if (pivotRow !== i) {
      for (let j = 0; j < n; j += 1) {
        const tmp = M[i * n + j];
        M[i * n + j] = M[pivotRow * n + j];
        M[pivotRow * n + j] = tmp;
      }
      const tmp = rhs[i];
      rhs[i] = rhs[pivotRow];
      rhs[pivotRow] = tmp;
    }
    const pivot = M[i * n + i];
    for (let j = i; j < n; j += 1) M[i * n + j] /= pivot;
    rhs[i] /= pivot;
    for (let k = 0; k < n; k += 1) {
      if (k === i) continue;
      const factor = M[k * n + i];
      if (factor === 0) continue;
      for (let j = i; j < n; j += 1) M[k * n + j] -= factor * M[i * n + j];
      rhs[k] -= factor * rhs[i];
    }
  }
  return rhs;
}

export function computeHomography(
  src: readonly [Point, Point, Point, Point],
  dst: readonly [Point, Point, Point, Point],
): Homography3x3 | null {
  const A = new Float64Array(8 * 8);
  const b = new Float64Array(8);
  for (let i = 0; i < 4; i += 1) {
    const { x, y } = src[i];
    const { x: u, y: v } = dst[i];
    const r1 = i * 2;
    const r2 = r1 + 1;
    A[r1 * 8 + 0] = x;
    A[r1 * 8 + 1] = y;
    A[r1 * 8 + 2] = 1;
    A[r1 * 8 + 3] = 0;
    A[r1 * 8 + 4] = 0;
    A[r1 * 8 + 5] = 0;
    A[r1 * 8 + 6] = -u * x;
    A[r1 * 8 + 7] = -u * y;
    b[r1] = u;
    A[r2 * 8 + 0] = 0;
    A[r2 * 8 + 1] = 0;
    A[r2 * 8 + 2] = 0;
    A[r2 * 8 + 3] = x;
    A[r2 * 8 + 4] = y;
    A[r2 * 8 + 5] = 1;
    A[r2 * 8 + 6] = -v * x;
    A[r2 * 8 + 7] = -v * y;
    b[r2] = v;
  }
  const h = gaussSolve(A, b, 8);
  if (!h) return null;
  const out = new Float64Array(9);
  for (let i = 0; i < 8; i += 1) out[i] = h[i];
  out[8] = 1;
  return out;
}
